/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, mergeMap, observeOn, skip, take } from 'rxjs/operators';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar';
import { Action, Store } from '@ngrx/store';
import { OrganisationCollaboratorInterface } from '../../../models/interface/organisation/organisation-collaborator.interface';
import { OrganisationSharedService } from '../../../services/shared/organisation-shared/organisation-shared.service';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { PermissionInterface } from '../../../models/interface/permission.interface';
import { RoleInterface } from '../../../models/interface/role.interface';
import { HttpResponse } from '@angular/common/http';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { Actions, ofType } from '@ngrx/effects';
import { GetOrganisationCollaboratorsSuccess, OrganisationActionTypes } from '../../../store/organisation/organisation.action';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { GetAllUsersAdminPayload, UserActionTypes } from '../../../store/user/user.action';
import { UserStore } from '../../../store/user/component-store/user.store';
import { OrganisationInterface } from 'src/app/models/interface/organisation/organisation.interface';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import { AuthenticationStore } from 'src/app/store/authentication/component-store/authentication.store';
import { AlertService } from 'src/app/services/alert/alert.service';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

@Component({
  selector: 'app-organisation-collaborator',
  templateUrl: './organisation-collaborator.component.html',
  styleUrls: ['./organisation-collaborator.component.scss'],
  providers: [UserStore, AuthenticationStore],
})
export class OrganisationCollaboratorComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  faStar = faStar;
  faEnvelope = faEnvelope;
  faCheckCircle = faCheckCircle;
  faTimesCircle = faTimesCircle;

  public isLoading$: Observable<boolean>;

  public myUserId: number;

  // Data provided by OrganisationService
  public collaborators: OrganisationCollaboratorInterface[] = [];
  public eCoaches: UserInterface[];

  // Filter
  public filter = { searchFilter: '' };
  public searchTextSubject = new BehaviorSubject<string>('');

  public pagedCollaborators$: Observable<OrganisationCollaboratorInterface[]>;

  public isCollapse$: Observable<boolean>;

  public collaborators$: Observable<UserInterface[]>;

  // Check if logged in user is organisation manager
  public isManager$: Observable<boolean>;

  // Check if logged in user is organisation owner
  public isOwner$: Observable<boolean>;

  // Translation
  public param = { intervention_name: '...' };

  public profile$: Observable<ProfileInterface>;
  public profileRoles$: Observable<RoleInterface[]>;

  public resendVerificationResponse$: Observable<any>;

  public isCollapseSubject = new BehaviorSubject<boolean>(true);

  private isModalLoadingSubject = new BehaviorSubject<boolean>(true);

  private users$: Observable<UserInterface[]>;

  // Organisation ID and loading status for this child component
  private organisationId: number;
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  private pagedCollaboratorsSubject = new BehaviorSubject<OrganisationCollaboratorInterface[]>([]);

  private collaboratorsSubject = new BehaviorSubject<OrganisationCollaboratorInterface[]>([]);

  private isManagerSubject = new BehaviorSubject<boolean>(false);

  private allCollaborators$: Observable<UserInterface[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private sharedService: OrganisationSharedService,
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private alertService: AlertService,
    private userStore: UserStore,
    private authenticationStore: AuthenticationStore,
    private actions$: Actions,
    private store: Store<{
      myProfile: ProfileInterface;
      myRoles: RoleInterface[];
      users: UserInterface[];
    }>,
  ) {
    this.profile$ = store.select('myProfile');
    this.profileRoles$ = store.select('myRoles');

    this.isLoading$ = this.isLoadingSubject.asObservable();

    this.pagedCollaborators$ = this.pagedCollaboratorsSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.collaborators$ = this.collaboratorsSubject.asObservable();
    this.isManager$ = this.isManagerSubject.asObservable();
    this.users$ = this.store.select('users');
    this.allCollaborators$ = this.userStore.allCollaborators$;
    this.resendVerificationResponse$ = this.authenticationStore.resendVerificationResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  ngOnInit(): void {
    // Sharing parent values: id and organisation name
    this.subscriptions.push(
      this.sharedService.sourceOrganisation$.subscribe((value: OrganisationInterface) => {
        if (value === null) {
          this.isLoadingSubject.next(true);
        } else {
          this.organisationId = value.id;
          this.param = { intervention_name: value.attributes.name };
          this.subscriptions.push(
            this.profile$
              .pipe(
                filter((user) => user !== null),
                take(1),
              )
              .subscribe({
                next: (result: ProfileInterface) => {
                  this.myUserId = result.id;
                  this.applyCollaboratorFilter();
                },
                error: (error) => {
                  throwError(() => new Error('Loading error')).pipe(observeOn(error));
                },
              }),
          );
        }
      }),
    );
  }

  public applyCollaboratorFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadCollaboratorList(true).subscribe(() => {
        this.isLoadingSubject.next(false);
      })
    );
  }

  public reloadCollaboratorList(setFirst?: boolean): Observable<boolean> {
    const pageIndex = this.paginator?.paginator ? this.paginator?.paginator.pageIndex : 0;
    const pageSize = this.paginator?.paginator ? this.paginator?.paginator.pageSize : 20;
    return this.profileRoles$.pipe(
      filter((user) => user !== null),
      mergeMap((roles: RoleInterface[]) => {
        const isAdmin = this.helperService.hasRoles(roles, /admin$/);
        this.store.dispatch({
          type: OrganisationActionTypes.getOrganisationCollaboratorsType,
          payload: { organisationId: this.organisationId },
        });
        return this.actions$.pipe(
          ofType(
            OrganisationActionTypes.getOrganisationCollaboratorsSuccessType,
            OrganisationActionTypes.getOrganisationCollaboratorsErrorType,
          ),
          take(1),
          mergeMap((action: Action) => {
            if (action.type === OrganisationActionTypes.getOrganisationCollaboratorsSuccessType) {
              const result: HttpResponse<any> = (action as GetOrganisationCollaboratorsSuccess).response;
              this.collaborators = result['body']['data'];
              // Determine my highest role
              const myself = this.collaborators.find((user) => user.id === this.myUserId);
              if (myself !== undefined) {
                const myRoleSlug = this.helperService.getHighestRoleOfOrganisationCollaborator(myself);
                const isOwner = myRoleSlug === 'organisation.owner';
                const isManager = isOwner || myRoleSlug === 'organisation.manager';
                this.isManagerSubject.next(isManager);
              }
            }
            if (isAdmin) {
              const payload: GetAllUsersAdminPayload = {
                roles: 'ecoach',
                isVerified: 1,
              };
              this.store.dispatch({ type: UserActionTypes.getAllUsersAdminType, payload });
              return this.users$.pipe(skip(1), take(1));
            } else {
              this.userStore.getAllCollaboratorsOfUser({ roles: 'ecoach' });
              return this.allCollaborators$.pipe(skip(1), take(1));
            }
          }),
          catchError(() => {
            if (isAdmin) {
              const payload: GetAllUsersAdminPayload = {
                roles: 'ecoach',
                isVerified: 1,
              };
              this.store.dispatch({ type: UserActionTypes.getAllUsersAdminType, payload });
              return this.users$.pipe(skip(1), take(1));
            } else {
              this.userStore.getAllCollaboratorsOfUser({ roles: 'ecoach' });
              return this.allCollaborators$.pipe(skip(1), take(1));
            }
          })
        );
      }),
      mergeMap((result) => {
        // All user with role editor
        this.eCoaches = this.getCollaboratorsNotInOrganisation(result, this.collaborators);

        // If user is not manager - they can only see organisation.manager and organisation.owner
        if (!this.isManagerSubject.value) {
          this.collaborators = this.collaborators.filter((user: OrganisationCollaboratorInterface) => {
            const includedRoles: PermissionInterface[] = user.attributes.roles;
            const foundManager = includedRoles.find(
              (role) => role.slug === 'organisation.manager' || role.slug === 'organisation.owner',
            );
            return foundManager !== undefined;
          });
        }
        return this.search(this.filter.searchFilter).pipe(
          mergeMap(() =>
            this.helper.setPagedContent(
              this.collaboratorsSubject,
              this.pagedCollaboratorsSubject,
              setFirst,
              pageIndex,
              pageSize,
            ),
          ),
        );
      }),
      catchError(
        (error) => throwError(() => new Error('Loading error')).pipe(observeOn(error)),
      ),
    );
  }

  public updatePagedCollaborators(event: OrganisationCollaboratorInterface[]) {
    if (event) {
      this.pagedCollaboratorsSubject.next(event);
    }
  }

  getFullName(collaborator: OrganisationCollaboratorInterface): string | undefined {
    if (!collaborator.attributes.firstname || !collaborator.attributes.lastname) {
      return undefined;
    }
    return collaborator.attributes.firstname + ' ' + collaborator.attributes.lastname;
  }

  public openDialogOrganisationCollaboratorAdd(): void {
    this.helperDialog
      .openDialogOrganisationCollaboratorAdd(this.param, this.organisationId, this.collaborators, this.eCoaches)
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  public openDialogOrganisationCollaboratorEdit(): void {
    this.helperDialog
      .openDialogOrganisationCollaboratorEdit(this.param, this.organisationId, this.collaborators, this.eCoaches)
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  private allowCollaboratorButton(collaborator: OrganisationCollaboratorInterface): boolean {
    return (
      this.isManagerSubject.value &&
      this.myUserId !== collaborator.id &&
      this.helper.getHighestRoleOfOrganisationCollaborator(collaborator) !== 'organisation.owner'
    );
  }

  showRemoveCollaborator(collaborator: OrganisationCollaboratorInterface): boolean {
    return this.allowCollaboratorButton(collaborator) && collaborator.attributes.is_verified === 1;
  }

  public openDialogOrganisationCollaboratorRemove(user: UserInterface): void {
    this.helperDialog
      .openDialogOrganisationCollaboratorRemove(this.param, this.organisationId, this.collaborators, this.eCoaches, user)
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  public openDialogOrganisationAccountCreation(): void {
    this.helperDialog
      .openDialogOrganisationAccountCreation([this.organisationId])
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  showDeleteUnverifiedAccount(collaborator: OrganisationCollaboratorInterface): boolean {
    return this.allowCollaboratorButton(collaborator) && collaborator.attributes.is_verified === 0;
  }

  public openDialogDeleteUnverified(user: UserInterface): void {
    this.helperDialog
      .openDialogUnverifiedOrganisationAccountDelete(this.organisationId, user, this.myUserId)
      .afterClosed()
      .pipe(mergeMap(() => this.reloadCollaboratorList()))
      .subscribe(() => {});
  }

  public openDialogPatientResendVerification(): void {
    const dialogRef = this.helperDialog.openDialogPatientResendVerification(this.isModalLoadingSubject);
    const loading = this.isModalLoadingSubject.asObservable().subscribe((value) => {
      if (dialogRef && dialogRef.componentInstance) {
        dialogRef.componentInstance.data = { submitted: value };
      }
    });
    dialogRef.afterClosed().subscribe(() => {
      loading.unsubscribe();
    });
  }

  showResendVerificationEmail(collaborator: OrganisationCollaboratorInterface): boolean {
    return this.allowCollaboratorButton(collaborator) && collaborator.attributes.is_verified === 0;
  }

  public resendVerificationEmail(email: string): void {
    this.isModalLoadingSubject.next(true);

    this.authenticationStore.resendVerification(email);
    this.resendVerificationResponse$
      .pipe(
        filter((value) => !!value),
        take(1),
      )
      .subscribe((value) => {
        if (value instanceof HttpResponse) {
          this.alertService.success('ACCOUNT_RESEND_SUCCEEDED');
        } else {
          this.alertService.error('ACCOUNT_RESEND_FAILED');
        }
        this.isModalLoadingSubject.next(false);
        this.openDialogPatientResendVerification();
      });
  }

  // Reads search value and runs searchFilter
  private search(name: string): Observable<boolean> {
    this.searchTextSubject.next(name);
    return this.searchTextSubject.pipe(
      distinctUntilChanged(),
      mergeMap(() => {
        this.collaboratorsSubject.next(
          this.helper.filterOrganisationCollaboratorsBy(
            this.collaborators,
            this.filter.searchFilter.toLowerCase().trim(),
          ),
        );
        return of(true);
      })
    );
  }

  // Get all eCoaches who are not part of the organisation
  private getCollaboratorsNotInOrganisation(
    allEditors: UserInterface[],
    myCollaborators: OrganisationCollaboratorInterface[],
  ): UserInterface[] {
    return allEditors.filter(
      (editor) => myCollaborators.every((collaborator) => editor.id !== collaborator.id),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
